import React, { useCallback, useEffect } from "react";
import type { BaseEntity, SerializedViewState } from "../../../UniversalCRMTable/types";
import { useGlobalModalStack } from "@wtc/ui";
import { useContexts } from "./hooks/useContexts";
import { useScrollState } from "./hooks/useScrollState";
import { useTabsData } from "./hooks/useTabsData";
import { useSavedViewTabPrefs } from "./hooks/useSavedViewTabPrefs";
import { filterVisibleTabs, orderTabs, mergeVisibleReorder } from "./utils";
import { SavedViewsTabsUI } from "./SavedViewsTabsUI";
import { SavedViewsModals } from "./SavedViewsModals";
import { SavedViewsModalProvider } from "./contexts/SavedViewsModalContext";
import type { SavedViewsTabsProps } from "./types";

export const SavedViewsTabsContainer = React.memo(function SavedViewsTabsContainer<
  T extends BaseEntity,
>({ savedViews, activeViewId, onViewChange, displayName }: SavedViewsTabsProps<T>) {
  const { context, filterContext } = useContexts<T>();
  const { closeModal } = useGlobalModalStack();
  const { scrollContainerRef, canScrollLeft, canScrollRight, scroll, setupScrollListeners } =
    useScrollState();

  const savedViewsState = context?.savedViewsState;
  const allViews = savedViewsState?.allViews || [];
  const { tabs, isUserCreatedView } = useTabsData(allViews);

  // Per-user server-persisted preferences (app_users.user_prefs): which pills are
  // shown, and their left-to-right order. Server-side so they survive logout and
  // follow the user across browsers/devices (not per-browser localStorage).
  const entityName = context?.pageConfig.entityName;
  const { isHidden, hideView, unhideView, orderedIds, setOrder } =
    useSavedViewTabPrefs(entityName);

  // Apply the saved order, then hide pills the user chose to hide — "Main" and
  // the active view always stay visible. See orderTabs / filterVisibleTabs.
  const orderedTabs = React.useMemo(
    () => orderTabs(tabs, orderedIds),
    [tabs, orderedIds]
  );
  const visibleTabs = filterVisibleTabs(orderedTabs, isHidden, activeViewId);

  // Canonical full order of real view ids (Main excluded) — drives the dialog
  // and the merge when the bar reorders only the visible subset.
  const fullViewOrder = React.useMemo(
    () =>
      orderedTabs
        .filter((t): t is { id: string; name: string } => t.id !== null)
        .map((t) => t.id),
    [orderedTabs]
  );

  const handleReorderVisible = useCallback(
    (visibleViewIds: string[]) => {
      setOrder(mergeVisibleReorder(fullViewOrder, visibleViewIds));
    },
    [setOrder, fullViewOrder]
  );

  // Handle tab click - applies saved view filters via FilterContext
  const handleTabClick = useCallback(
    (viewId: string | null) => {
      // Use FilterContext to apply the saved view
      // This replaces all current filters and updates the URL
      if (filterContext) {
        filterContext.actions.applyPreset(viewId);
      } else {
        // Fallback to onViewChange if FilterContext is not available
        onViewChange(viewId);
      }
    },
    [filterContext, onViewChange]
  );

  // Initialize scroll check on mount and when allViews change
  useEffect(() => {
    return setupScrollListeners(allViews.length);
  }, [setupScrollListeners, allViews.length]);

  // Don't render if no saved views
  if (tabs.length <= 1) {
    return null;
  }

  // Get the currently selected view
  const selectedView = savedViewsState?.selectedViewId
    ? allViews.find((v) => v.id === savedViewsState.selectedViewId)
    : undefined;

  return (
    <>
      <SavedViewsTabsUI
        tabs={visibleTabs}
        views={allViews}
        fields={context?.pageConfig.config.fields ?? {}}
        activeViewId={activeViewId}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        scrollContainerRef={scrollContainerRef}
        onScroll={scroll}
        onTabClick={handleTabClick}
        isUserCreatedView={isUserCreatedView}
        isFavorite={(viewId: string): boolean => {
          if (!savedViewsState?.favorites) return false;
          return savedViewsState.favorites.includes(viewId);
        }}
        onToggleFavorite={(viewId: string) => {
          if (savedViewsState) {
            const isFav = savedViewsState.favorites.includes(viewId);
            savedViewsState.toggleFavorite.mutate({
              viewId,
              favorite: !isFav,
            });
          }
        }}
        onShare={(viewId: string) => {
          if (savedViewsState) {
            savedViewsState.handleShare(viewId);
          }
        }}
        onDelete={(viewId: string) => {
          if (savedViewsState) {
            savedViewsState.handleDelete(viewId);
          }
        }}
        onAddView={() => {
          if (savedViewsState) {
            savedViewsState.handleSaveAs();
          }
        }}
        showAddButton={!!savedViewsState}
        isViewHidden={isHidden}
        onHideView={hideView}
        onUnhideView={unhideView}
        orderedViewIds={fullViewOrder}
        onReorderVisible={handleReorderVisible}
        onReorderAll={setOrder}
      />

      {/* Modals wrapped with context provider */}
      {savedViewsState && (
        <SavedViewsModalProvider
          value={{
            selectedView: selectedView as any,
            selectedViewId: savedViewsState.selectedViewId,
            resource: context?.pageConfig.entityName || "",
            entityName: context?.pageConfig.entityName,
            createViewMutation: savedViewsState.createView,
            updateViewMutation: savedViewsState.updateView,
            deleteViewMutation: savedViewsState.deleteView,
            serializeFilters: savedViewsState.serializeFilters,
            saveAsName: savedViewsState.saveAsName,
            setSaveAsName: savedViewsState.setSaveAsName,
            shareOpen: savedViewsState.shareOpen,
            setShareOpen: savedViewsState.setShareOpen,
            shareVisibility: savedViewsState.shareVisibility,
            setShareVisibility: savedViewsState.setShareVisibility,
            shareUserIds: savedViewsState.shareUserIds,
            setShareUserIds: (ids: string[] | ((prev: string[]) => string[])) => {
              const newIds = typeof ids === "function" 
                ? ids(savedViewsState.shareUserIds)
                : ids;
              savedViewsState.setShareUserIds(newIds);
            },
            deleteOpen: savedViewsState.deleteOpen,
            setDeleteOpen: (open: boolean) => {
              savedViewsState.setDeleteOpen(open);
              if (!open) savedViewsState.setDeletingViewId(null);
            },
            onShare: () => {
              const viewToShare = savedViewsState.allViews.find(
                (v) => v.id === savedViewsState.selectedViewId
              );
              if (!viewToShare || !("created_by" in viewToShare)) return;

              savedViewsState.updateView.mutate(
                {
                  id: viewToShare.id,
                  resource: context?.pageConfig.entityName || "",
                  updates: {
                    visibility: savedViewsState.shareVisibility,
                    shared_with_user_ids:
                      savedViewsState.shareVisibility === "shared"
                        ? savedViewsState.shareUserIds
                        : [],
                  },
                },
                { onSuccess: () => closeModal("tabs-share-view") }
              );
            },
            onDelete: () => {
              const viewToDelete = savedViewsState.allViews.find(
                (v) => v.id === savedViewsState.deletingViewId
              );
              if (!viewToDelete || !("created_by" in viewToDelete)) return;

              savedViewsState.deleteView.mutate(
                {
                  id: viewToDelete.id,
                  resource: context?.pageConfig.entityName || "",
                },
                {
                  onSuccess: () => {
                    closeModal("tabs-delete-view");
                    savedViewsState.setDeletingViewId(null);
                    onViewChange(null);
                  },
                }
              );
            },
          }}
        >
          <SavedViewsModals />
        </SavedViewsModalProvider>
      )}
    </>
  );
}) as <T extends BaseEntity>(props: SavedViewsTabsProps<T>) => React.ReactElement;
